import React from 'react';
import { Card, Switch, Typography } from 'antd';
import { ExperimentOutlined } from '@ant-design/icons';

import useTestMode from '../../models/useTestMode';

import { useClientDetails } from './useClientDetails';
import { TEST_MODE_COLOR, TEST_PILL_RED } from './TestModeBanner';

const { Text } = Typography;

/**
 * The Test Mode switch — Partner page only, isDeveloper clients only.
 * Flipping it swaps THIS BROWSER into the test world (viewing lens, nothing
 * server-side changes) and navigates home so every screen reconstructs its
 * queries in the new world.
 */
export const TestModeSwitchCard = ({ clientId, adminView = false }) => {
  const client = useClientDetails(clientId);
  const isDeveloper = !!(client && client.isDeveloper);
  const [on, setOn] = useTestMode(clientId);

  // Partners flip their own switch; a Strike Admin (adminView, rendered only
  // on the admin route) can inspect ANY client's test world — partner test
  // activity lands on real clients, and admin must be able to see it.
  if (!isDeveloper && !adminView) return null;

  const flip = (next) => {
    setOn(next);
    // Class screens read the world at construction — a navigation rebuilds
    // them all in the new world. Partners land on their home; an admin
    // stays on the admin surface they flipped from.
    if (adminView) {
      window.location.reload();
    } else {
      window.location.assign(`/client/manage/${clientId}`);
    }
  };

  return (
    <Card
      title={(
        <span>
          <ExperimentOutlined style={{ color: TEST_MODE_COLOR, marginRight: 8 }} />
          Test Mode
        </span>
      )}
      style={{ marginBottom: '16px', textAlign: 'left', borderTop: `3px solid ${TEST_MODE_COLOR}` }}
      data-testid="test-mode-switch-card"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Switch
          checked={on}
          onChange={flip}
          style={on ? { backgroundColor: TEST_PILL_RED } : undefined}
          data-testid="test-mode-switch"
        />
        <Text>
          {on
            ? 'This browser is in TEST MODE — activity lists show test transactions only.'
            : (adminView
              ? "View this client in TEST MODE: their lists as a test-mode partner sees them. Flipping opens the client's pages in Test Mode."
              : 'Swap this browser into TEST MODE: activity lists show test transactions only, and the menu changes colour so the mode is unmistakable.')}
        </Text>
      </div>
      <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
        A viewing lens for this browser only — nothing changes for other users
        of this account, and live payments are untouched.
      </Text>
    </Card>
  );
};

export default TestModeSwitchCard;
